/**
 * 이벤트 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { eventService } from '../../services/eventService';
import { Event, EventFilter } from '../../types/event';

const EventsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<EventFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['events', filter],
    queryFn: () => eventService.getEvents(filter),
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">진행중</Badge>;
      case 'ended':
        return <Badge bg="secondary">종료</Badge>;
      default:
        return <Badge bg="warning">비활성</Badge>;
    }
  };

  const columns: Column<Event>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'event_name',
      label: '이벤트명',
      render: (event) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/events/${event.id}`)}
        >
          {event.event_name}
        </span>
      ),
    },
    {
      key: 'event_code',
      label: '이벤트 코드',
      render: (event) => (
        <span style={{ color: '#ccc' }}>{event.event_code}</span>
      ),
    },
    {
      key: 'event_type',
      label: '유형',
      render: (event) => (
        <span style={{ color: '#ccc' }}>{event.event_type || '-'}</span>
      ),
    },
    {
      key: 'start_date',
      label: '시작일',
      render: (event) => (
        <span style={{ color: '#ccc' }}>
          {new Date(event.start_date).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
    {
      key: 'end_date',
      label: '종료일',
      render: (event) => (
        <span style={{ color: '#ccc' }}>
          {new Date(event.end_date).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (event) => getStatusBadge(event.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '이벤트명, 코드로 검색',
    },
    {
      name: 'event_type',
      label: '유형',
      type: 'text',
      placeholder: '이벤트 유형으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '진행중' },
        { value: 'inactive', label: '비활성' },
        { value: 'ended', label: '종료' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (event: Event) => {
    navigate(`/admin/events/${event.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>이벤트 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/events/new')}>
            이벤트 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.events || []}
              loading={isLoading}
              emptyMessage="이벤트가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default EventsList;

